import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import { collectionQueryKeys } from '../collections/collections.query'
import { productQueryKeys } from '../products/products.query'
import { getInventory, updateInventory, type InventoryOrganizationScope } from './inventory.api'
import { inventoryQueryKeys } from './inventory.keys'
import type { UpdateInventoryInput } from './inventory.types'

/** Hooks fall back to the route's organization; the key builder throws without one. */
interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const inventoryQueries = {
  index: ({ organizationId }: InventoryOrganizationScope) =>
    queryOptions({
      queryKey: inventoryQueryKeys.index({ organizationId }),
      queryFn: getInventory,
    }),
}

export function useInventory({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof inventoryQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...inventoryQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

/** Stock changes what products and collections can sell. */
export function useUpdateInventoryMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateInventoryInput) => updateInventory({ organizationId, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: inventoryQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: productQueryKeys.all({ organizationId }) }),
        queryClient.invalidateQueries({ queryKey: collectionQueryKeys.all({ organizationId }) }),
      ]),
  })
}
